import { Autocomplete, FormControl, FormHelperText, TextField } from "@mui/material";
import { orderBy } from "lodash-es";
import { useEffect, useMemo, type FC } from "react";
import type { NoNullFields } from "../../../../server/supabase/base-supabase-service";
import type { UsableAccountPersonField } from "../../server/supabase-segment-field-service";
import type { Segment, SegmentMode } from "./types";

interface SegmentAudienceProps {
  segment: Segment["segment_json"]["segments"][number];
  segmentFields: NoNullFields<UsableAccountPersonField>[];
  handleFieldChange: (value: string | null) => void;
  disabled?: boolean;
  errMsg?: string;
  mode: SegmentMode;
}

export const SegmentAudience: FC<SegmentAudienceProps> = ({
  segment,
  segmentFields,
  handleFieldChange,
  disabled,
  errMsg,
  mode,
}) => {
  const audiences = useMemo(
    () =>
      orderBy(
        segmentFields.filter((f) => f.key.startsWith("recipient_type_")),
        (f) => f.display_name.toLowerCase(),
      ),
    [segmentFields],
  );

  const value = useMemo(() => {
    const val = audiences.find((f) => f.key === segment.recipient_type_key);
    return val ?? null;
  }, [segment.recipient_type_key, audiences]);

  useEffect(() => {
    if (!value && mode === "SEGMENT") {
      const firstUsable = audiences.find((x) => x.usable);
      if (firstUsable) {
        handleFieldChange(firstUsable.key);
      }
    }
  }, [value, mode, audiences, handleFieldChange]);

  return (
    <FormControl sx={{ mr: 1, minWidth: 250 }} error={Boolean(errMsg)}>
      <Autocomplete
        size="small"
        disablePortal={false}
        options={audiences}
        getOptionLabel={(option) => option.display_name}
        disableClearable={mode === "SEGMENT"}
        onChange={(_, val) => {
          if (!val) {
            if (mode === "SEGMENT") return;
            handleFieldChange(null);
            return;
          }

          handleFieldChange(val.key);
        }}
        value={value}
        disabled={disabled}
        renderInput={(params) => <TextField {...params} label="Audience" variant="outlined" />}
      />
      {Boolean(errMsg) && <FormHelperText>{errMsg}</FormHelperText>}
    </FormControl>
  );
};
